import { toast } from 'sonner';
import { tryRefreshToken } from './refreshInterceptor';

const API_BASE = '/api';

export type ProblemDetails = {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  correlationId?: string;
  errors?: Record<string, string[]>;
};

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem: ProblemDetails,
    public readonly correlationId: string | null,
  ) {
    super(problem.title ?? `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

// `silent: true` suprime o toast automático em erros não-401. Usado por
// chamadas fire-and-forget (markDocumentViewed/Downloaded), em que o erro
// é caller-irrelevant — o caller já tem `.catch` swallow e não quer poluir
// a UI com toast vermelho quando o backend retorna 404/403 esperado (ex.:
// admin abre doc em área que não tem perm explícita — view tracking não
// se aplica, mas não é "erro" pro usuário).
type RequestInitJson = Omit<RequestInit, 'body'> & { body?: unknown; silent?: boolean };

async function request<T>(path: string, init: RequestInitJson = {}, _retried = false): Promise<T> {
  const headers = new Headers(init.headers);

  const isFormData = init.body instanceof FormData;
  if (init.body !== undefined && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }


  const body =
    init.body === undefined || isFormData
      ? (init.body as BodyInit | undefined)
      : JSON.stringify(init.body);

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    body,
    credentials: 'include',
  });

  if (response.status === 401 && !_retried && !path.startsWith('/auth/')) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      return request<T>(path, init, true);
    }
  }

  if (!response.ok) {
    const correlationId = response.headers.get('X-Correlation-Id');
    let problem: ProblemDetails = { status: response.status };
    try {
      const data = await response.json();
      if (data && typeof data === 'object') {
        problem = { ...data, status: response.status };
      }
    } catch {
      // sem corpo JSON, segue com o que temos
    }

    if (response.status === 401) {
      const publicPaths = ['/login', '/register', '/confirm-email', '/reset-password', '/forgot-password', '/mfa/']
      const isPublic = publicPaths.some(p => window.location.pathname.startsWith(p))
      if (!isPublic) {
        window.location.href = '/login';
      }
      throw new ApiError(response.status, problem, correlationId);
    }

    if (!init.silent) {
      toast.error(problem.title ?? `Erro ${response.status}`, {
        description: [problem.detail, correlationId && `id: ${correlationId}`]
          .filter(Boolean)
          .join(' • '),
      });
    }

    throw new ApiError(response.status, problem, correlationId);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('Content-Type') ?? '';
  if (contentType.includes('application/json')) {
    return (await response.json()) as T;
  }
  return (await response.text()) as T;
}

export const api = {
  get: <T>(path: string, init?: RequestInitJson) => request<T>(path, { ...init, method: 'GET' }),
  post: <T>(path: string, body?: unknown, init?: RequestInitJson) =>
    request<T>(path, { ...init, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, init?: RequestInitJson) =>
    request<T>(path, { ...init, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, init?: RequestInitJson) =>
    request<T>(path, { ...init, method: 'PATCH', body }),
  delete: <T>(path: string, init?: RequestInitJson) =>
    request<T>(path, { ...init, method: 'DELETE' }),
};
